import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, AlertTriangle, Info, Music, DollarSign, Users, Globe, Youtube, Image, Headphones, Code, FileText, User, Activity, Shield } from 'lucide-react';
import '../styles/FAQ.css';

const FAQ = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const renderIcon = (iconName) => {
    const iconSize = 20;
    const iconClass = 'section-icon';
    
    const icons = {
      pricing: <DollarSign size={iconSize} className={iconClass} />,
      plans: <Users size={iconSize} className={iconClass} />,
      distribution: <Globe size={iconSize} className={iconClass} />,
      youtube: <Youtube size={iconSize} className={iconClass} />,
      different: <Check size={iconSize} className={iconClass} />,
      covers: <Image size={iconSize} className={iconClass} />,
      audio: <Headphones size={iconSize} className={iconClass} />,
      code: <Code size={iconSize} className={iconClass} />,
      license: <FileText size={iconSize} className={iconClass} />,
      contentId: <Shield size={iconSize} className={iconClass} />,
      artist: <User size={iconSize} className={iconClass} />,
      streaming: <Activity size={iconSize} className={iconClass} />,
      account: <Info size={iconSize} className={iconClass} />,
    };

    return icons[iconName] || <Info size={iconSize} className={iconClass} />;
  };

  const sections = [
    {
      id: 'pricing',
      title: 'Pricing & Payments',
      icon: 'pricing',
      content: (
        <div className="section-content">
          <h3>Simple. Transparent. One-Time Fees.</h3>
          <ul className="faq-list">
            <li><strong>Is this a subscription?</strong> No. You pay once. Upload forever.</li>
            <li><strong>Will my music be taken down if I don't pay again?</strong> No recurring fees.</li>
            <li><strong>How does Falphin make money?</strong> Revenue share.</li>
            <li><strong>When do I get paid?</strong> Once balance reaches $100.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'plans',
      title: 'Artist & Label Plans',
      icon: 'plans',
      content: (
        <div className="section-content plans-grid">
          <div className="plan-card">
            <h3>🎤 Artist Plans</h3>
            
            <div className="plan-tier">
              <h4>Starter Artist — $4.99 <span className="badge">One-Time</span></h4>
              <ul>
                <li>Up to 20 tracks or 1 album</li>
                <li>1 artist or group</li>
                <li>Global distribution</li>
                <li>Royalty tracking</li>
                <li>YouTube Official Art Tracks</li>
              </ul>
            </div>

            <div className="plan-tier pro">
              <h4>Pro Artist — $14.99 <span className="badge">One-Time</span></h4>
              <ul>
                <li>Unlimited uploads</li>
                <li>1 artist or group</li>
                <li>Global distribution</li>
                <li>Royalty tracking</li>
                <li>YouTube Official Art Tracks</li>
                <li>Priority processing</li>
              </ul>
            </div>
          </div>

          <div className="plan-card label-plan">
            <h3>🏷 Label Plan</h3>
            <div className="plan-tier">
              <h4>Label — $49.99 <span className="badge">One-Time</span></h4>
              <ul>
                <li>Unlimited artists</li>
                <li>Unlimited uploads</li>
                <li>Centralized label dashboard</li>
                <li>Global distribution</li>
                <li>Built to scale</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'distribution',
      title: 'Distribution & Platforms',
      icon: 'distribution',
      content: (
        <div className="section-content">
          <div className="platforms-grid">
            {['Spotify', 'Apple Music', 'YouTube Music', 'TikTok', 'Facebook', 
              'Audiomack', 'Deezer', 'Amazon Music', 'TIDAL', 'Anghami',
              'Beatport', 'Beatsource', 'Traxsource', 'SoundCloud'].map(platform => (
              <div key={platform} className="platform-tag">
                {platform}
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'youtube',
      title: 'Earn From YouTube Without Monetization',
      icon: 'youtube',
      content: (
        <div className="section-content">
          <div className="feature-highlight">
            <h3>You focus on creating. We handle the infrastructure.</h3>
            <ul className="feature-list">
              <li>Official Art Tracks & Topic Channels</li>
              <li>No monetized channel required</li>
              <li>Revenue from music rights</li>
              <li>Global royalty collection</li>
              <li>Automatic payouts</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'different',
      title: 'Why Falphin Is Different',
      icon: 'different',
      content: (
        <div className="section-content">
          <ul className="checkmark-list">
            <li>One-time account creation fee</li>
            <li>Upload forever</li>
            <li>Manual review & approval</li>
            <li>Secure royalty handling</li>
            <li>Built for serious artists & labels</li>
            <li>No spam, no low-quality uploads</li>
          </ul>
        </div>
      )
    },
    {
      id: 'covers',
      title: '⚠️ Cover Image Guidelines',
      icon: 'covers',
      isWarning: true,
      content: (
        <div className="section-content">
          <ul className="guideline-list">
            <li>Square image only</li>
            <li>Recommended: 3000×3000 (Min 1400×1400)</li>
            <li>No stretching, no low resolution</li>
            <li>Must match artist & album name</li>
            <li>No URLs, socials, or contacts</li>
            <li>No explicit imagery</li>
            <li>No misleading artist names</li>
            <li>No third-party logos/trademarks</li>
            <li>Must be copyright free</li>
          </ul>
        </div>
      )
    },
    // Additional sections for other guidelines...
    {
      id: 'audio',
      title: 'Audio Requirements',
      icon: 'audio',
      content: (
        <div className="section-content">
          <ul className="warning-list">
            <li>No copyrighted samples, vocals, loops</li>
            <li>Minimum 16-bit / 44.1 kHz</li>
            <li>Stereo only</li>
            <li>Minimum 60 seconds</li>
            <li>NO AI-generated content</li>
            <li>Vesta Music currently does not support AI-generated music</li>
          </ul>
        </div>
      )
    },
    {
      id: 'code',
      title: 'Code Allocation (ISRC & UPC)',
      icon: 'code',
      content: (
        <div className="section-content">
          <ul className="guideline-list">
            <li>Existing releases → transfer codes</li>
            <li>Do NOT create new ISRCs</li>
            <li>ISRCs must match actual songs</li>
            <li>No fake or algorithm-boosting codes</li>
            <li>Must follow international ISRC format</li>
            <li>Custom prefix → contact account rep</li>
          </ul>
        </div>
      )
    },
    {
      id: 'license',
      title: 'Licensing Rules',
      icon: 'license',
      content: (
        <div className="section-content">
          <ul className="guideline-list">
            <li>Proper licensing required for all third-party content</li>
            <li>Lease agreements required for beats</li>
            <li>Sample sources must be provided</li>
            <li>Remix license required</li>
            <li>Mechanical license required for covers</li>
            <li>Documents must be verifiable</li>
          </ul>
        </div>
      )
    },
    {
      id: 'contentId',
      title: 'Content ID Guidelines',
      icon: 'contentId',
      content: (
        <div className="section-content">
          <p><strong>100% owned content ONLY</strong></p>
          <p>No leased, public, or purchased beats</p>
          
          <div className="warning-box">
            <h4>Disallowed:</h4>
            <ul className="disallowed-list">
              <li>Watermarked audio</li>
              <li>Remixes & covers</li>
              <li>Creative Commons / Public Domain</li>
              <li>Samples & loops</li>
              <li>Sound effects</li>
              <li>Audiobooks</li>
              <li>Podcasts</li>
              <li>Spoken word</li>
            </ul>
            <p className="warning-text">No parasiting behavior allowed</p>
          </div>
        </div>
      )
    },
    {
      id: 'artist',
      title: 'Artist Guidelines',
      icon: 'artist',
      content: (
        <div className="section-content">
          <ul className="guideline-list">
            <li>Upload only under artists you control</li>
            <li>Popular artists may require verification</li>
            <li>Name conflicts may occur — contact support</li>
            <li>Composer & lyricist must be legal names</li>
          </ul>
        </div>
      )
    },
    {
      id: 'streaming',
      title: 'Artificial Streaming Policy',
      icon: 'streaming',
      content: (
        <div className="section-content">
          <div className="warning-box">
            <ul className="warning-list">
              <li>Client responsible for penalties</li>
              <li>Spotify $10 artificial stream fee applies</li>
              <li>Artificially streamed content removed</li>
              <li>Never buy streams or playlist placement</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'account',
      title: 'Account Status & Enforcement',
      icon: 'account',
      content: (
        <div className="section-content">
          <ul className="warning-list">
            <li>Platform access can be revoked</li>
            <li>Severe violations may terminate the deal</li>
            <li>Content ID abuse results in full removal</li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="faq-container">
      <header className="faq-header">
        <h1>Falpin Guidelines & FAQ</h1>
        <p>Everything you need to know about distributing your music with Falphin</p>
      </header>
      
      <div className="faq-sections">
        {sections.map((section) => (
          <div 
            key={section.id} 
            className={`faq-section ${openSection === section.id ? 'open' : ''} ${section.isWarning ? 'warning' : ''}`}
          >
            <button 
              className="section-header"
              onClick={() => toggleSection(section.id)}
              aria-expanded={openSection === section.id}
            >
              <div className="section-title">
                {renderIcon(section.icon)}
                <h2>{section.title}</h2>
              </div>
              <span className="toggle-icon">
                {openSection === section.id ? '−' : '+'}
              </span>
            </button>
            
            {openSection === section.id && (
              <div className="section-content-wrapper">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="faq-footer">
        <p>Need help? <Link to="/contact" className="contact-link">Contact our support team</Link></p>
      </div>
    </div>
  );
};

export default FAQ;
